import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Play, Pause, SkipForward, SkipBack, Repeat, Gauge, Headphones } from 'lucide-react';
import { apiCall, speak, stopSpeaking } from './api';
import { toast } from 'sonner';

export default function ListeningPlaylist({ onBack }) {
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState('');
  const [items, setItems]     = useState([]);
  const [idx, setIdx]         = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop]       = useState(false);
  const [rate, setRate]       = useState(0.9);
  const playingRef = useRef(false);

  useEffect(() => { apiCall('/modules').then(setModules).catch(()=>{}); }, []);

  const load = async (mid) => {
    setModuleId(mid); setIdx(0);
    const r = await apiCall(`/listening/playlist${mid ? `?module_id=${mid}` : ''}`);
    setItems(r.items || []);
  };

  useEffect(() => () => { playingRef.current = false; stopSpeaking(); }, []);

  const playNext = useCallback(async (i) => {
    if (!playingRef.current || !items[i]) return;
    setIdx(i);
    await speak(items[i].text, 'en-US', rate);
    if (!playingRef.current) return;
    // brief pause then next
    await new Promise(r => setTimeout(r, 700));
    if (i + 1 < items.length) {
      playNext(i + 1);
    } else if (loop) {
      playNext(0);
    } else {
      playingRef.current = false; setPlaying(false);
      toast.success('Playlist terminée');
    }
  }, [items, rate, loop]);

  const start = () => {
    if (!items.length) return toast.error('Choisissez un module');
    playingRef.current = true; setPlaying(true);
    playNext(idx);
  };
  const pause = () => { playingRef.current = false; setPlaying(false); stopSpeaking(); };
  const skip = (delta) => {
    pause();
    const ni = Math.max(0, Math.min(items.length - 1, idx + delta));
    setIdx(ni);
    setTimeout(() => { playingRef.current = true; setPlaying(true); playNext(ni); }, 100);
  };

  const current = items[idx];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-8" data-testid="listening-playlist">
      <button onClick={() => { pause(); onBack(); }} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />Retour</button>
      <div className="flex items-center gap-2 mb-2"><Headphones size={20} className="text-emerald-300" /><h2 className="text-2xl font-bold">Mode écoute libre</h2></div>
      <p className="text-white/60 text-sm mb-6">Lecture continue, écran libre. Idéal pour habituer ton oreille à l'anglais.</p>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4">
        <label className="text-xs text-white/60 block mb-2">Choisir un parcours</label>
        <select value={moduleId} onChange={e => load(e.target.value)} data-testid="playlist-module"
          className="w-full bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm">
          <option value="">— Tous les modules —</option>
          {modules.map(m => <option key={m.id} value={m.id}>{m.title_fr}</option>)}
        </select>
      </div>

      {items.length > 0 && current && (
        <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-emerald-500/30 rounded-3xl p-6">
          <div className="text-xs text-emerald-300 mb-2">{current.lesson_title} · {current.level}</div>
          <div className="text-2xl font-bold mb-2">{current.text}</div>
          <div className="text-sm text-white/60">{current.translation}</div>

          <div className="flex items-center justify-center gap-3 mt-6">
            <button onClick={() => skip(-1)} data-testid="pl-prev" className="p-3 bg-white/10 hover:bg-white/20 rounded-full"><SkipBack size={18} /></button>
            {playing
              ? <button onClick={pause} data-testid="pl-pause" className="p-4 bg-emerald-500 text-slate-950 rounded-full"><Pause size={24} /></button>
              : <button onClick={start} data-testid="pl-play" className="p-4 bg-emerald-500 text-slate-950 rounded-full"><Play size={24} /></button>}
            <button onClick={() => skip(1)} data-testid="pl-next" className="p-3 bg-white/10 hover:bg-white/20 rounded-full"><SkipForward size={18} /></button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <label className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2">
              <Gauge size={14} className="text-emerald-300" />
              <select value={rate} onChange={e => setRate(Number(e.target.value))} data-testid="pl-rate" className="bg-transparent text-xs flex-1">
                <option value="0.7">Lent</option>
                <option value="0.9">Normal</option>
                <option value="1.1">Rapide</option>
              </select>
            </label>
            <label className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2 cursor-pointer">
              <Repeat size={14} className={loop ? 'text-emerald-300' : 'text-white/40'} />
              <input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} data-testid="pl-loop" />
              <span className="text-xs">Lecture en boucle</span>
            </label>
          </div>

          <div className="text-xs text-white/40 text-center mt-4">{idx + 1} / {items.length}</div>
        </div>
      )}

      {items.length === 0 && moduleId && <div className="text-center text-white/40 py-10 text-sm">Aucun audio disponible.</div>}
    </motion.div>
  );
}
